import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { Slider } from "../ui/slider";
import { AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { Fold } from "./Section";
import { FontPicker } from "./FontPicker";
import { StyledText } from "../site/StyledText";

const SWATCH = ["#FFFFFF", "#F5F0E6", "#D4AF37", "#C9A27E", "#E8C872", "#B8860B", "#94A3B8", "#1E293B", "#DC2626", "#0284C7", "#16A34A", "#7C3AED"];

function ColorPick({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {SWATCH.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)} aria-label={c} data-testid={`${testid}-swatch-${c.slice(1)}`} style={{ background: c }}
          className={`w-6 h-6 rounded-full border border-stone-300 transition-transform hover:scale-110 ${value === c ? "ring-2 ring-offset-1 ring-stone-900" : ""}`} />
      ))}
      <input type="color" value={value || "#D4AF37"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="w-9 h-7 cursor-pointer" data-testid={`${testid}-picker`} />
      <code className="text-[11px] text-stone-500">{value || "varsayılan"}</code>
    </div>
  );
}

function PartColor({ label, value = {}, onChange, testid }) {
  const upd = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-stone-600"><Switch checked={!!value.grad} onCheckedChange={upd("grad")} data-testid={`${testid}-grad`} />Renk geçişi</label>
          {value.c1 && <button type="button" onClick={() => onChange({})} className="text-xs text-stone-500 hover:text-stone-900 underline" data-testid={`${testid}-reset`}>Varsayılan</button>}
        </div>
      </div>
      <ColorPick value={value.c1} onChange={upd("c1")} testid={`${testid}-c1`} />
      {value.grad && <div className="pl-3 border-l-2"><p className="text-xs text-stone-500 mb-1">Geçişin bitiş rengi</p><ColorPick value={value.c2} onChange={upd("c2")} testid={`${testid}-c2`} /></div>}
    </div>
  );
}

const ALIGNS = [["", "Varsayılan", null], ["left", "Sola", AlignLeft], ["center", "Ortaya", AlignCenter], ["right", "Sağa", AlignRight]];

function SizeAlign({ v, set, testid }) {
  return (
    <>
      <div className="space-y-2" data-testid={`${testid}-size`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Yazı boyutu</span>
          <div className="flex items-center gap-3">
            <code className="text-xs text-stone-500" data-testid={`${testid}-size-value`}>{v.size ? `${v.size}px` : "varsayılan"}</code>
            {v.size && <button type="button" onClick={() => set("size")(undefined)} className="text-xs text-stone-500 hover:text-stone-900 underline" data-testid={`${testid}-size-reset`}>Varsayılan</button>}
          </div>
        </div>
        <Slider min={10} max={120} step={1} value={[v.size || 32]} onValueChange={([x]) => set("size")(x)} data-testid={`${testid}-size-slider`} />
        <div className="flex flex-wrap gap-1.5">
          {[12, 16, 24, 32, 40, 48, 64, 80].map((n) => (
            <button key={n} type="button" onClick={() => set("size")(n)} data-testid={`${testid}-size-${n}`}
              className={`px-2 py-0.5 rounded border text-xs transition-colors ${v.size === n ? "bg-stone-900 text-white border-stone-900" : "hover:bg-stone-100"}`}>{n}</button>
          ))}
        </div>
        <p className="text-xs text-stone-400">Mobilde otomatik olarak orantılı küçülür.</p>
      </div>
      <div className="space-y-2">
        <span className="text-sm font-medium">Konum (hizalama)</span>
        <div className="inline-flex rounded-md border bg-white p-0.5" data-testid={`${testid}-align`}>
          {ALIGNS.map(([k, l, Icon]) => (
            <button key={l} type="button" onClick={() => set("align")(k || undefined)} data-testid={`${testid}-align-${k || "default"}`}
              className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 transition-colors ${(v.align || "") === k ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>
              {Icon && <Icon size={14} />}{l}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export function TextStyleEditor({ title, value, onChange, sample = "", tail = 1, parts = ["İlk kısım", "Son kısım"], testid }) {
  const v = value || {};
  const set = (k) => (x) => onChange({ ...v, [k]: x });
  return (
    <Fold title={title.replace("(yazı tipi ve renk)", "(yazı tipi, renk, boyut, konum)")} meta={[v.font || "Tema varsayılanı", v.size && `${v.size}px`, v.align && { left: "sola", center: "ortada", right: "sağa" }[v.align]].filter(Boolean).join(" · ")} testid={testid}>
      <div className="rounded-lg bg-[#0B0C10] px-5 py-4 text-3xl text-[#C9A27E] font-heading overflow-hidden" data-testid={`${testid}-preview`}>
        <StyledText text={sample || "Örnek Başlık"} style={v} tail={tail} />
      </div>
      <Tabs defaultValue="font">
        <TabsList>
          <TabsTrigger value="font" data-testid={`${testid}-tab-font`}>Yazı tipi</TabsTrigger>
          <TabsTrigger value="color" data-testid={`${testid}-tab-color`}>Renk</TabsTrigger>
          <TabsTrigger value="layout" data-testid={`${testid}-tab-layout`}>Boyut ve konum</TabsTrigger>
        </TabsList>
        <TabsContent value="font"><FontPicker value={v.font} onChange={set("font")} sample={sample} /></TabsContent>
        <TabsContent value="layout" className="space-y-5">
          <SizeAlign v={v} set={set} testid={testid} />
        </TabsContent>
        <TabsContent value="color" className="space-y-3">
          <PartColor label={parts[0]} value={v.a} onChange={set("a")} testid={`${testid}-a`} />
          <PartColor label={parts[1]} value={v.b} onChange={set("b")} testid={`${testid}-b`} />
        </TabsContent>
      </Tabs>
    </Fold>
  );
}
